/**
 * 6ᵉ étape du wizard — les informations recueillies au devis (§01, §02, §04 de
 * la Project Identity Sheet).
 *
 * `value === undefined` = case décochée. Cocher écrit `{}` : la PRÉSENCE de la
 * clé tient lieu de drapeau, ce qui supprime tout risque qu'un booléen
 * contredise son contenu.
 *
 * Aucun champ n'est obligatoire et l'étape reste franchissable vide — le
 * créateur du projet n'a pas toujours le devis sous la main.
 */
import { Box, Checkbox, Chip, FormControlLabel, MenuItem, Select, Stack, TextField, Typography } from '@mui/material';
import { Timestamp } from 'firebase/firestore';
import { useTranslation } from 'react-i18next';
import {
  AP_CONNECTIVITY, AP_FEASIBILITY, AP_MILESTONE_KEYS, AP_SCOPES, AP_TECHNOLOGIES,
  type ApFeasibility, type ApMilestoneKey, type AvantProjet, type User,
} from '@altyor-kronos/shared-types';
import ApRiskTable from '../ApRiskTable';

export interface StepAvantProjetProps {
  value: AvantProjet | undefined;
  /** Pour le sélecteur d'auteur du devis — `quotationOwner` est un uid. */
  users: Array<{ id: string; data: User }>;
  onChange: (next: AvantProjet | undefined) => void;
}

/** Bascule une valeur dans un tableau de puces — sélection multiple. */
function toggle<T>(list: T[] | undefined, v: T): T[] {
  const cur = list ?? [];
  return cur.includes(v) ? cur.filter((x) => x !== v) : [...cur, v];
}

export default function StepAvantProjet({ value, users, onChange }: StepAvantProjetProps) {
  const { t } = useTranslation();
  const ap = value;
  const patch = (p: Partial<AvantProjet>) => onChange({ ...(ap ?? {}), ...p });

  const setDate = (key: ApMilestoneKey, iso: string) =>
    patch({
      baselineDates: {
        ...(ap?.baselineDates ?? {}),
        [key]: iso ? Timestamp.fromDate(new Date(iso)) : undefined,
      },
    });

  return (
    <Box sx={{ py: 2 }}>
      <FormControlLabel
        control={
          <Checkbox
            checked={ap !== undefined}
            onChange={(e) => onChange(e.target.checked ? {} : undefined)}
            slotProps={{
              input: { 'data-testid': 'ap-toggle' } as React.InputHTMLAttributes<HTMLInputElement>,
            }}
          />
        }
        label={t('projects.avantProjetTab.toggle')}
      />

      {ap && (
        <Stack spacing={3} sx={{ mt: 2 }}>
          {/* §01 — contexte */}
          <Stack spacing={2}>
            <Typography variant="h4">{t('projects.avantProjetTab.context')}</Typography>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
              <TextField fullWidth size="small" label={t('projects.avantProjetTab.apReference')}
                value={ap.apReference ?? ''} onChange={(e) => patch({ apReference: e.target.value })}
                slotProps={{ htmlInput: { 'data-testid': 'ap-reference' } }} />
              <TextField fullWidth size="small" label={t('projects.avantProjetTab.clientContact')}
                value={ap.clientContact ?? ''} onChange={(e) => patch({ clientContact: e.target.value })} />
              <TextField fullWidth size="small" label={t('projects.avantProjetTab.market')}
                value={ap.market ?? ''} onChange={(e) => patch({ market: e.target.value })} />
            </Stack>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
              <TextField fullWidth size="small" label={t('projects.avantProjetTab.clientSpecRef')}
                value={ap.clientSpecRef ?? ''} onChange={(e) => patch({ clientSpecRef: e.target.value })} />
              <TextField fullWidth size="small" label={t('projects.avantProjetTab.productType')}
                value={ap.productType ?? ''} onChange={(e) => patch({ productType: e.target.value })} />
              <Select fullWidth size="small" displayEmpty value={ap.feasibility ?? ''}
                onChange={(e) => patch({ feasibility: (e.target.value as ApFeasibility) || undefined })}
                SelectDisplayProps={{ 'data-testid': 'ap-feasibility' } as React.HTMLAttributes<HTMLDivElement>}>
                <MenuItem value="">{t('projects.avantProjetTab.feasibilityEmpty')}</MenuItem>
                {AP_FEASIBILITY.map((f) => (
                  <MenuItem key={f} value={f}>{t(`projects.avantProjetTab.feasibilityValue.${f}`)}</MenuItem>
                ))}
              </Select>
            </Stack>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
              {/* Auteur du devis : uid, pas du texte libre — c'est un salarié Altyor,
                  et un uid reste joignable le jour où le CRM reprend la main. */}
              <Select fullWidth size="small" displayEmpty value={ap.quotationOwner ?? ''}
                onChange={(e) => patch({ quotationOwner: e.target.value || undefined })}
                SelectDisplayProps={{ 'data-testid': 'ap-quotation-owner' } as React.HTMLAttributes<HTMLDivElement>}>
                <MenuItem value="">{t('projects.avantProjetTab.quotationOwnerEmpty')}</MenuItem>
                {users.map((u) => (
                  <MenuItem key={u.id} value={u.id}>{u.data.displayName}</MenuItem>
                ))}
              </Select>
              <TextField fullWidth type="date" size="small"
                label={t('projects.avantProjetTab.transferDate')}
                slotProps={{ inputLabel: { shrink: true }, htmlInput: { 'data-testid': 'ap-transfer-date' } }}
                value={ap.transferDate?.toDate().toISOString().split('T')[0] ?? ''}
                onChange={(e) => patch({
                  transferDate: e.target.value ? Timestamp.fromDate(new Date(e.target.value)) : undefined,
                })} />
            </Stack>
            <TextField fullWidth multiline minRows={2} size="small"
              label={t('projects.avantProjetTab.needDescription')}
              value={ap.needDescription ?? ''} onChange={(e) => patch({ needDescription: e.target.value })} />
            <Box>
              <Typography variant="caption">{t('projects.avantProjetTab.altyorScope')}</Typography>
              <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap', rowGap: 0.5, mt: 0.5 }}>
                {AP_SCOPES.map((s) => (
                  <Chip key={s} size="small" clickable
                    color={ap.altyorScope?.includes(s) ? 'primary' : 'default'}
                    variant={ap.altyorScope?.includes(s) ? 'filled' : 'outlined'}
                    label={t(`projects.avantProjetTab.scope.${s}`)}
                    onClick={() => patch({ altyorScope: toggle(ap.altyorScope, s) })} />
                ))}
              </Stack>
            </Box>
          </Stack>

          {/* §02 — cibles */}
          <Stack spacing={2}>
            <Typography variant="h4">{t('projects.avantProjetTab.targets')}</Typography>
            <Typography variant="caption" color="text.secondary">
              {t('projects.avantProjetTab.baselineHelp')}
            </Typography>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ flexWrap: 'wrap' }}>
              {AP_MILESTONE_KEYS.map((k) => (
                <TextField key={k} type="date" size="small"
                  label={t(`projects.avantProjetTab.milestone.${k}`)}
                  slotProps={{ inputLabel: { shrink: true }, htmlInput: { 'data-testid': `ap-date-${k}` } }}
                  value={ap.baselineDates?.[k]?.toDate().toISOString().split('T')[0] ?? ''}
                  onChange={(e) => setDate(k, e.target.value)} />
              ))}
            </Stack>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
              <TextField fullWidth size="small" type="number" label={t('projects.avantProjetTab.targetProductCost')}
                value={ap.targetProductCost ?? ''}
                onChange={(e) => patch({ targetProductCost: e.target.value ? Number(e.target.value) : undefined })} />
              <TextField fullWidth size="small" type="number" label={t('projects.avantProjetTab.targetSellPrice')}
                value={ap.targetSellPrice ?? ''}
                onChange={(e) => patch({ targetSellPrice: e.target.value ? Number(e.target.value) : undefined })} />
            </Stack>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
              {(['year1', 'year2', 'year3'] as const).map((y) => (
                <TextField key={y} fullWidth size="small" type="number"
                  label={t(`projects.avantProjetTab.volume.${y}`)}
                  value={ap.volumes?.[y] ?? ''}
                  onChange={(e) => patch({
                    volumes: { ...(ap.volumes ?? {}), [y]: e.target.value ? Number(e.target.value) : undefined },
                  })} />
              ))}
            </Stack>
            <Box>
              <Typography variant="caption">{t('projects.avantProjetTab.technologies')}</Typography>
              <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap', rowGap: 0.5, mt: 0.5 }}>
                {AP_TECHNOLOGIES.map((x) => (
                  <Chip key={x} size="small" clickable
                    color={ap.technologies?.includes(x) ? 'primary' : 'default'}
                    variant={ap.technologies?.includes(x) ? 'filled' : 'outlined'}
                    label={t(`projects.avantProjetTab.tech.${x}`)}
                    onClick={() => patch({ technologies: toggle(ap.technologies, x) })} />
                ))}
              </Stack>
            </Box>
            <Box>
              <Typography variant="caption">{t('projects.avantProjetTab.connectivity')}</Typography>
              <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap', rowGap: 0.5, mt: 0.5 }}>
                {AP_CONNECTIVITY.map((x) => (
                  <Chip key={x} size="small" clickable
                    color={ap.connectivity?.includes(x) ? 'primary' : 'default'}
                    variant={ap.connectivity?.includes(x) ? 'filled' : 'outlined'}
                    label={t(`projects.avantProjetTab.conn.${x}`)}
                    onClick={() => patch({ connectivity: toggle(ap.connectivity, x) })} />
                ))}
              </Stack>
            </Box>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
              <TextField fullWidth size="small" label={t('projects.avantProjetTab.certifications')}
                value={ap.certifications ?? ''} onChange={(e) => patch({ certifications: e.target.value })} />
              <TextField fullWidth size="small" label={t('projects.avantProjetTab.targetMarkets')}
                value={ap.targetMarkets ?? ''} onChange={(e) => patch({ targetMarkets: e.target.value })} />
            </Stack>
          </Stack>

          {/* §04 — risques */}
          <Stack spacing={1}>
            <Typography variant="h4">{t('projects.avantProjetTab.risks')}</Typography>
            <ApRiskTable risks={ap.risks ?? []} canEdit onChange={(risks) => patch({ risks })} />
          </Stack>
        </Stack>
      )}
    </Box>
  );
}
